import { memo, useCallback, useMemo, useState } from "react";
import type { CraftableWeaponSummary } from "@/lib/crafting/types";
import { assetUrl } from "@/lib/bungie/assets";
import { ItemTooltip } from "./ItemTooltip";
import {
  activeFilterCount,
  EMPTY_FILTERS,
  facetsFor,
  filterWeapons,
  toggleValue,
  type CatalogueFilters,
} from "@/lib/manifest/catalogue";

export interface WeaponCatalogueProps {
  weapons: CraftableWeaponSummary[];
  manifestVersion?: string;
  onOpen: (recipeHash: number) => void;
  onBack?: () => void;
}

/**
 * The pattern-selection screen.
 *
 * Follows the in-game SHAPE menu (see reference/VISUAL_SPEC.md): a left rail of
 * weapon-type categories, a compact multi-column grid of icon-and-name tiles,
 * and a detail card at the top right for whatever is highlighted.
 *
 * Two deliberate departures: the game has no search or element filters here, but
 * 219 patterns on a web page need them, so they live in the rail styled as the
 * game's icon tab row. And the category rows toggle rather than being strictly
 * single-select — visually identical, but you can stack two.
 */
export function WeaponCatalogue({
  weapons,
  manifestVersion,
  onOpen,
  onBack,
}: WeaponCatalogueProps) {
  const [filters, setFilters] = useState<CatalogueFilters>(EMPTY_FILTERS);
  // Identity only — the tooltip tracks the pointer itself, so moving the mouse
  // never re-renders this component or its hundreds of tiles.
  const [hovered, setHovered] = useState<{
    weapon: CraftableWeaponSummary;
    initial: { x: number; y: number };
  } | null>(null);

  const facets = useMemo(() => facetsFor(weapons), [weapons]);
  const results = useMemo(() => filterWeapons(weapons, filters), [weapons, filters]);
  const activeCount = activeFilterCount(filters);

  // Stable, so the memoized tiles are not invalidated on every render.
  const highlight = useCallback(
    (weapon: CraftableWeaponSummary, initial: { x: number; y: number }) =>
      setHovered({ weapon, initial }),
    []
  );
  const clearHighlight = useCallback(() => setHovered(null), []);

  return (
    <div className="mx-auto max-w-[110rem] px-4 py-5 sm:px-8">
      {/* The item tooltip follows the pointer, like the in-game inventory hover. */}
      {hovered && <ItemTooltip weapon={hovered.weapon} initial={hovered.initial} />}

      <div className="mb-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="label-caps mb-2 text-xs text-ink-2 transition-colors hover:text-ink"
          >
            ← Back
          </button>
        )}
        <h1 className="type-display text-3xl leading-none sm:text-4xl">
          Shape
          <span className="sr-only"> — choose a craftable weapon pattern</span>
        </h1>
        <p className="label-caps mt-1 text-sm text-ink-2">{weapons.length} patterns</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
        {/* --- left rail ----------------------------------------------------
            Sticky so the filters stay reachable while scrolling 219 patterns.
            `self-start` is load-bearing: grid items stretch to the row height by
            default, which leaves nothing for sticky to travel within. The
            max-height plus internal scroll keeps the whole rail usable when the
            type list is taller than the viewport. */}
        <aside className="space-y-4 lg:sticky lg:top-5 lg:max-h-[calc(100dvh-2.5rem)] lg:self-start lg:overflow-y-auto lg:pr-1">
          <div>
            <label htmlFor="weapon-search" className="sr-only">
              Search weapons
            </label>
            <input
              id="weapon-search"
              type="search"
              value={filters.search}
              placeholder="Search…"
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className="w-full border border-line-dim bg-black/50 px-3 py-2 text-sm focus:border-line"
            />
          </div>

          {/* Icon tab rows, in place of the game's category tabs. */}
          <IconTabs
            legend="Element"
            options={facets.elements.map((e) => ({ value: e.name, label: e.name, icon: e.icon }))}
            selected={filters.elements}
            onToggle={(value) =>
              setFilters((f) => ({ ...f, elements: toggleValue(f.elements, value) }))
            }
          />
          <IconTabs
            legend="Ammo"
            options={facets.ammoTypes.map((a) => ({ value: a.value, label: a.label }))}
            selected={filters.ammoTypes}
            onToggle={(value) =>
              setFilters((f) => ({ ...f, ammoTypes: toggleValue(f.ammoTypes, value) }))
            }
          />

          {/* Weapon-type category list — the rail's centrepiece in game. */}
          <fieldset>
            <legend className="label-caps mb-1.5 text-xs text-ink-3">Weapon type</legend>
            <div className="border border-line-dim">
              {facets.weaponTypes.map((type) => {
                const isOn = filters.weaponTypes.includes(type);
                const count = weapons.filter((w) => w.itemTypeDisplayName === type).length;
                return (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={isOn}
                    onClick={() =>
                      setFilters((f) => ({ ...f, weaponTypes: toggleValue(f.weaponTypes, type) }))
                    }
                    className={[
                      "label-caps flex w-full items-center justify-between gap-2 border-b border-line-dim/60 px-3 py-2 text-left text-xs transition-colors last:border-b-0",
                      isOn ? "bg-white/16 text-ink" : "text-ink-2 hover:bg-white/8",
                    ].join(" ")}
                  >
                    <span className="truncate">{type}</span>
                    {/* The game marks rows that have content with a diamond. */}
                    <span aria-hidden className="shrink-0 text-[0.6rem] text-ink-3">
                      {count > 0 ? "◆" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {activeCount > 0 && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="label-caps w-full border border-line-dim px-3 py-2 text-xs transition-colors hover:bg-white/10"
            >
              Clear filters ({activeCount})
            </button>
          )}

          {manifestVersion && (
            <p className="text-[0.65rem] break-all text-ink-3">Manifest {manifestVersion}</p>
          )}
        </aside>

        {/* --- pattern grid -------------------------------------------------- */}
        <section aria-label="Weapon results">
          <p aria-live="polite" className="label-caps mb-2 text-xs text-ink-3">
            {results.length === weapons.length
              ? `${results.length} patterns`
              : `${results.length} of ${weapons.length} patterns`}
          </p>

          {results.length === 0 ? (
            <div className="corner-brackets border border-line-dim bg-black/40 px-6 py-16 text-center">
              <p className="text-ink-2">No patterns match those filters.</p>
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="label-caps mt-4 border border-line-dim px-3 py-2 text-xs transition-colors hover:bg-white/10"
              >
                Clear filters
              </button>
            </div>
          ) : (
            // Separators rather than gaps, matching the game's dense grid.
            <ul className="grid grid-cols-1 gap-px bg-line-dim/40 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {results.map((weapon) => (
                <li key={weapon.recipeHash}>
                  <PatternTile
                    weapon={weapon}
                    active={hovered?.weapon.recipeHash === weapon.recipeHash}
                    onOpen={onOpen}
                    onHighlight={highlight}
                    onLeave={clearHighlight}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * Memoized: hovering changes `active` on at most two tiles, so without this a
 * single hover would re-render all 219.
 */
const PatternTile = memo(function PatternTile({
  weapon,
  active,
  onOpen,
  onHighlight,
  onLeave,
}: {
  weapon: CraftableWeaponSummary;
  active: boolean;
  onOpen: (recipeHash: number) => void;
  onHighlight: (weapon: CraftableWeaponSummary, initial: { x: number; y: number }) => void;
  onLeave: () => void;
}) {
  const icon = assetUrl(weapon.icon);

  return (
    <button
      type="button"
      onClick={() => onOpen(weapon.recipeHash)}
      // Enter only — the tooltip follows the pointer itself from here, so there
      // is deliberately no onMouseMove handler.
      onMouseEnter={(e) => onHighlight(weapon, { x: e.clientX, y: e.clientY })}
      onMouseLeave={onLeave}
      // Keyboard users get the same tooltip, anchored to the tile itself since
      // there is no pointer position to follow.
      onFocus={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onHighlight(weapon, { x: r.right - 18, y: r.bottom - 18 });
      }}
      onBlur={onLeave}
      // The type and element live in the tooltip, but a screen-reader user
      // moving through the grid needs them on the tile itself.
      aria-label={`${weapon.name} — ${[weapon.damageTypeName, weapon.itemTypeDisplayName]
        .filter(Boolean)
        .join(" ")}`}
      className={[
        "relative flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition-colors",
        active ? "bg-white/16" : "bg-black/45 hover:bg-white/10",
      ].join(" ")}
    >
      {active && (
        <span aria-hidden className="pointer-events-none absolute inset-0 border border-line" />
      )}
      {icon ? (
        <img src={icon} alt="" aria-hidden className="h-10 w-10 shrink-0" />
      ) : (
        <span aria-hidden className="h-10 w-10 shrink-0 bg-white/10" />
      )}
      <span className="label-caps min-w-0 flex-1 truncate text-xs font-semibold">
        {weapon.name}
      </span>
    </button>
  );
});

function IconTabs<T extends string | number>({
  legend,
  options,
  selected,
  onToggle,
}: {
  legend: string;
  options: { value: T; label: string; icon?: string }[];
  selected: readonly T[];
  onToggle: (value: T) => void;
}) {
  if (options.length === 0) return null;
  return (
    <fieldset>
      <legend className="label-caps mb-1.5 text-xs text-ink-3">{legend}</legend>
      <div className="flex flex-wrap gap-1">
        {options.map((option) => {
          const isOn = selected.includes(option.value);
          const icon = assetUrl(option.icon);
          return (
            <button
              key={String(option.value)}
              type="button"
              aria-pressed={isOn}
              title={option.label}
              onClick={() => onToggle(option.value)}
              className={[
                "flex items-center gap-1 border px-1.5 py-1 text-[0.65rem] transition-colors",
                isOn
                  ? "border-line bg-white/16 text-ink"
                  : "border-line-dim text-ink-2 hover:bg-white/8",
              ].join(" ")}
            >
              {icon && <img src={icon} alt="" aria-hidden className="h-3.5 w-3.5" />}
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
